import { authService } from './authService';
import { getServerURL } from './config';

const SERVER_URL = getServerURL();

const request = async (path, options = {}) => {
  const headers = {
    ...authService.getAuthHeaders(),
    ...(options.headers || {})
  };
  if (typeof FormData !== 'undefined' && options.body instanceof FormData) {
    delete headers['Content-Type'];
    delete headers['content-type'];
  }
  const response = await fetch(`${SERVER_URL}${path}`, {
    ...options,
    headers
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload.success === false) {
    const error = new Error(payload.error || `Accounting request failed: ${response.status}`);
    error.code = payload.code;
    error.status = response.status;
    throw error;
  }
  return payload;
};

const buildQuery = (filters = {}) => {
  const query = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.set(key, value);
    }
  });
  const serialized = query.toString();
  return serialized ? `?${serialized}` : '';
};

const createIdempotencyKey = (prefix) => (
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`
);

const uploadPayoutFile = (path, file, { fields = {}, idempotencyKey } = {}) => {
  const formData = new FormData();
  formData.append('file', file);
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      formData.append(key, value);
    }
  });
  return request(path, {
    method: 'POST',
    headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
    body: formData
  });
};

export const accountingOperationsService = {
  async getSummary(filters = {}) {
    return request(`/accounting/summary${buildQuery(filters)}`);
  },

  async getPayouts(filters = {}) {
    return request(`/accounting/payouts${buildQuery(filters)}`);
  },

  async getEligibleOrders(filters = {}) {
    return request(`/accounting/payouts/eligible-orders${buildQuery(filters)}`);
  },

  async previewPaymobPayoutCsv(file) {
    return uploadPayoutFile('/accounting/payouts/paymob-csv-preview', file);
  },

  async previewBostaPayoutXlsx(file) {
    return uploadPayoutFile('/accounting/payouts/bosta-xlsx-preview', file);
  },

  async createPaymobPayoutCsv(
    file,
    data,
    idempotencyKey = createIdempotencyKey('payout')
  ) {
    return uploadPayoutFile('/accounting/payouts/paymob-csv', file, {
      fields: data,
      idempotencyKey
    });
  },

  async createBostaPayoutXlsx(file, data, idempotencyKey = createIdempotencyKey('payout')) {
    const fields = typeof data === 'string' ? { notes: data } : data;
    return uploadPayoutFile('/accounting/payouts/bosta-xlsx', file, {
      fields,
      idempotencyKey
    });
  },

  async createPayout(data, idempotencyKey = createIdempotencyKey('payout')) {
    return request('/accounting/payouts', {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify(data)
    });
  },

  async collectPayout(payoutId) {
    return request(`/accounting/payouts/${encodeURIComponent(payoutId)}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status: 'collected' })
    });
  }
};
